import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { DialogTitle, Popover } from "@headlessui/react";
import {
  ButtonTypeEnum,
  IMessage,
  useMarkNotificationsAs,
  useRemoveNotification
} from "@novu/notification-center";
import GetHeroIcon from "../../../utils/getHeroIcon";
import TelemetryButton from "../../atoms/button";
import { NotifyError } from "./notification";
import { IMessagePayload } from "./customNotification";
import LayoutSkeleton from "./LayoutSkeleton";
import { checkExpiry, getTimeDiffTextFromDate2 } from "./utility";
import useTracking from "../analytics/hooks/useTracking";
import { useLazyNotificationActionQuery } from "./slice/notificationSlice";
import ErrorListAlerts from "../../../pages/event-creation-workflow/errorListAlert";
import errorCodes from "../../../assets/config/message-code-fe.json";
import Modal from "../../atoms/modal";
import Button from "../../atoms/button";

interface NotifyItemProps {
  message: IMessage;
  onActionButtonClick?: (
    templateIdentifier: string,
    actionButtonType: ButtonTypeEnum,
    message: IMessage
  ) => void;
  onNotificationClick?: (message: IMessage) => void;
  onRemoveClick?: () => any;
  onUnseenClick?: () => any;
  setNotifyErrors: Dispatch<SetStateAction<Partial<NotifyError>[] | []>>;
  refetchCount: () => any;
  isLastNotification: boolean;
}

const getErrorMessage = (errorCodeList: any) => {
  const list = errorCodeList.map((err: any) => {
    return err.split("_")[0];
  });
  return list.map((err: keyof typeof errorCodes) => errorCodes[err]);
};

function NotifyItem({
  message,
  onNotificationClick,
  onUnseenClick,
  onRemoveClick,
  onActionButtonClick,
  refetchCount,
  isLastNotification
}: NotifyItemProps) {
  const [track] = useTracking();
  const [srvErr, setSrvErr] = useState<any>([]);
  const [removePopUpOpen, setRemovePopUpOpen] = useState(false);
  const [createAgo, setCreateAgo] = useState(
    getTimeDiffTextFromDate2(message?.createdAt)
  );
  const { isDarkMode } = useSelector((state: any) => state.DarkmodeSlice);

  const { removeNotification, isLoading: isRemovingNotification } =
    useRemoveNotification({
      onSuccess: () => {
        refetchCount();
        // console.log("message removeAllNotifications");
      },
      onError: function () {
        // no op
      }
    });

  const { markNotificationsAs, isLoading: isMarkingNotification } =
    useMarkNotificationsAs({
      onSuccess: () => {
        refetchCount();
      }
    });

  const [notificationAction, { isLoading }] = useLazyNotificationActionQuery();

  function getAlerts(srvErr: any) {
    const alerts: any = [];
    srvErr.forEach((notfiyError: any) => {
      alerts.push(
        <ErrorListAlerts
          severity={notfiyError?.severity}
          label={notfiyError?.label}
          messageList={notfiyError?.messageList}
          isNotification={true}
          // autoHideDuration={3000}
          timeoutCallback={() => setSrvErr([])}
          actionButtonClick={() => {
            setSrvErr([]);
          }}
        />
      );
    });
    return alerts;
  }

  const handleOnActionClick = async (
    _templateIdentifier: string,
    type: ButtonTypeEnum,
    message: IMessage
  ) => {
    try {
      const { payload }: any = message;

      const messagePayload = payload as unknown as IMessagePayload;
      const redirectUrl =
        messagePayload?.primaryUrl || messagePayload?.redirectUrl;
      const isHandleAction = messagePayload?.isBackendUrl;
      if (isHandleAction) {
        const { primaryUrl, secondaryUrl, headers, requestType, body } =
          messagePayload;
        const notificationResult = await notificationAction({
          url: type === ButtonTypeEnum.PRIMARY ? primaryUrl : secondaryUrl,
          method: requestType,
          headers,
          body
        }).unwrap();
        setSrvErr([
          {
            severity: "success",
            label: "Success!",
            messageList: getErrorMessage([notificationResult?.data?.message])
          }
        ]);
      }

      markNotificationsAs(
        { messageId: message._id, read: true, seen: true },
        {
          onSuccess() {
            if (redirectUrl && !isHandleAction) {
              window.location.href = redirectUrl;
            }
          },
          onError() {
            setSrvErr([
              {
                label: "Failed",
                severity: "error",
                messageList: [
                  "Error while reading notification"
                ] as unknown as []
              }
            ]);
          }
        }
      );
    } catch (error: any) {
      markNotificationsAs(
        { messageId: message._id, read: true, seen: true },
        {
          onSuccess() {
            // removeNotification({messageId: message._id})
          },
          onError() {
            setSrvErr([
              {
                label: "Failed",
                severity: "error",
                messageList: [
                  "Error while reading notification"
                ] as unknown as []
              }
            ]);
          }
        }
      );

      if (Array.isArray(error?.data?.error)) {
        setSrvErr([
          {
            severity: "error",
            label: "Failed!",
            messageList: getErrorMessage(error?.data?.error)
          }
        ]);
      }
    }
  };

  const handleOnNotificationClick = (notification: IMessage) => {
    try {
      const { payload }: any = notification;
      const messagePayload = payload as unknown as IMessagePayload;
      const redirectUrl = messagePayload?.primaryUrl;
      const isHandleAction = messagePayload.isBackendUrl;

      // TODO Can we have action call when there will be no buttons
      if (!isHandleAction) {
        markNotificationsAs(
          {
            messageId: notification._id,
            read: !isHandleAction,
            seen: !isHandleAction
          },
          {
            onSuccess() {
              if (redirectUrl && !isHandleAction) {
                window.location.href = redirectUrl;
              }
            },
            onError() {
              setSrvErr([
                {
                  label: "Failed",
                  severity: "error",
                  messageList: [
                    "Error while reading notification"
                  ] as unknown as []
                }
              ]);
            }
          }
        );
      }
    } catch (error: any) {
      if (Array.isArray(error?.data?.error)) {
        setSrvErr([
          {
            severity: "error",
            label: "Failed!",
            messageList: getErrorMessage(error?.data?.error)
          }
        ]);
      }
    }
    track("onClick", {
      eventName: "onClick",
      object: {
        id: notification._id,
        objectType: "NOTIFICATION"
      },
      verb: {
        id: "NOTIFICATION_READ",
        display: {
          "en-US": notification.content as string
        }
      }
    });
  };

  const isMessageExpired = useCallback(checkExpiry, []);

  return (
    <>
      {(isRemovingNotification || isMarkingNotification || isLoading) &&
      !message.read ? (
        <LayoutSkeleton />
      ) : (
        <div
          className={`w-full
    ${!message.read ? "border-l-4 border-l-primary dark:border-l-secondary" : ""} 
    rounded-md
    p-4
    ${message?.cta?.action?.buttons?.length ? "" : "hover:cursor-pointer"}
    border-4-
    `}
          style={{
            background:
              message.read && isDarkMode
                ? "#3a3b3c"
                : message.read
                  ? "#F5F8FA"
                  : !message.read && isDarkMode
                    ? "linear-gradient(90deg, #2a2a2a 0%, #4d4d4d 100%)"
                    : "linear-gradient(90deg, rgba(178, 223, 206, 0.5) 0%, rgba(237, 237, 237, 0.5) 100%)"
          }}
          role="tab"
          onClick={(e) => {
            e.stopPropagation();
            if (onNotificationClick) {
              // onNotificationClick(message);
            } else {
              handleOnNotificationClick(message);
            }
          }}
          onMouseEnter={() => {
            setCreateAgo(getTimeDiffTextFromDate2(message?.createdAt));
          }}
          data-testid="notificationLayoutClick"
        >
          <div
            className="text-sm break-words"
            dangerouslySetInnerHTML={{
              __html: String(message?.content).replaceAll("&amp;", "&")
            }}
          ></div>
          <div className="flex items-center justify-between group">
            <div className="italic text-sm font-extralight"> {createAgo}</div>
            <div className="flex items-center gap-2">
              <Popover className="relative">
                {({ close }) => (
                  <>
                    <div
                      className="invisible group-hover:visible group-hover:cursor-pointer"
                      onMouseLeave={() => {
                        close();
                      }}
                    >
                      <Popover.Button
                        className="outline-none"
                        data-testid="notificationPopoverMenu"
                      >
                        <GetHeroIcon
                          name="EllipsisHorizontalIcon"
                          className="w-8 aspect-square"
                        />
                      </Popover.Button>
                      <Popover.Panel
                        className={`absolute text-sm z-10 flex flex-col w-48 gap-3 p-2 rounded-lg shadow-sm -left-36 ${isLastNotification ? "-top-28" : ""} bg-bkg`}
                      >
                        <div
                          className="flex items-end gap-2 p-2 rounded-lg hover:bg-primary hover:text-white"
                          role="tab"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onUnseenClick) {
                              onUnseenClick();
                            } else {
                              markNotificationsAs({
                                messageId: message._id,
                                read: !message.read,
                                seen: !message.read
                              });
                              if (message.read) {
                                track("onClick", {
                                  eventName: "onClick",
                                  object: {
                                    id: message._id,
                                    objectType: "NOTIFICATION"
                                  },
                                  verb: {
                                    id: "NOTIFICATION_UNREAD",
                                    display: {
                                      "en-US": "Mark as Unread"
                                    }
                                  }
                                });
                              } else {
                                track("onClick", {
                                  eventName: "onClick",
                                  object: {
                                    id: message._id,
                                    objectType: "NOTIFICATION"
                                  },
                                  verb: {
                                    id: "NOTIFICATION_READ",
                                    display: {
                                      "en-US": "Mark as Read"
                                    }
                                  }
                                });
                              }
                            }
                          }}
                          data-testid="markNotificationRead"
                        >
                          <div>
                            <GetHeroIcon
                              name="ChatBubbleBottomCenterIcon"
                              className="w-6 aspect-square"
                              variant="outline"
                            />
                          </div>
                          <div className="">
                            Mark as {message.read ? "Unread" : "Read"}
                          </div>
                        </div>
                        <div
                          className="flex items-end gap-2 p-2 rounded-md hover:bg-primary hover:text-white"
                          role="tab"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRemovePopUpOpen(true);
                          }}
                          data-testid={`removeNotification`}
                        >
                          <div>
                            <GetHeroIcon
                              name="TrashIcon"
                              className="w-6 aspect-square"
                              variant="outline"
                            />
                          </div>
                          <div className="">Remove Message</div>
                        </div>
                      </Popover.Panel>
                    </div>
                  </>
                )}
              </Popover>
              {!message.read && (
                <div className="h-1 rounded-full aspect-square bg-primary group-hover:invisible" />
              )}
            </div>
            <Modal
              dialogueClassName="sm:max-w-3xl"
              open={removePopUpOpen}
              setOpen={setRemovePopUpOpen}
              children={
                <>
                  <div className=" w-full ">
                    <div className="my-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <DialogTitle
                        as="h3"
                        className="text-xl font-semibold leading-6 text-gray-900 dark:text-gray-50 mt-7"
                      >
                        Are you sure you want to remove this notification?
                      </DialogTitle>
                      <div className="my-4 text-gray-500 dark:text-gray-700">
                        You'll not be able to undo this action
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 pt-4 sm:mt-4 mr-2 flex gap-2 justify-end space-x-2 md:mb-3 mb-5">
                    <Button
                      label="Confirm"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onRemoveClick) {
                          // props.onRemoveClick();
                        } else {
                          removeNotification({
                            messageId: message._id
                          });
                        }
                        track("onClick", {
                          eventName: "onClick",
                          object: {
                            id: message._id,
                            objectType: "NOTIFICATION"
                          },
                          verb: {
                            id: "NOTIFICATION_DELETE",
                            display: {
                              "en-US": "Remove Message"
                            }
                          }
                        });
                        setRemovePopUpOpen(false);
                      }}
                      variant="contained"
                      buttonColor="primary"
                      testId="removeBtn"
                    />
                  </div>
                </>
              }
            />
          </div>
          <div className="flex gap-2">
            {message?.cta?.action?.buttons?.map((btn, index) => (
              <TelemetryButton
                key={index}
                className={`w-full font-bold ${
                  // btn.type === ButtonTypeEnum.PRIMARY
                  //   ? "bg-primary text-white  hover:bg-primary/90":
                  "bg-bkg text-primary hover:bg-gray-200/80"
                } text-xs py-1 rounded-md `}
                onClick={async (e) => {
                  e.stopPropagation();
                  if (onActionButtonClick) {
                    // onActionButtonClick(message._templateId, btn.type, message);
                  } else {
                    await handleOnActionClick(
                      message._templateId,
                      btn.type,
                      message
                    );
                  }
                }}
                data-testid="notificationAction"
                disabled={
                  message.templateIdentifier === "assesment-invitation"
                    ? false
                    : srvErr?.length > 0 || message?.read
                }
                statements={[
                  {
                    eventName: "onClick",
                    object: {
                      id: message._id,
                      objectType: "NOTIFICATION"
                    },
                    verb: {
                      id: "ATT_MARKED",
                      display: {
                        "en-US": btn.content as string
                      }
                    }
                  }
                ]}
              >
                {btn.content}
              </TelemetryButton>
            ))}
          </div>
        </div>
      )}
      {srvErr?.length > 0 && getAlerts(srvErr)}
    </>
  );
}

export default NotifyItem;
